import {
  AloftTime,
  addressToMultilineAddress,
  formatAppraisalType,
  formatShortenedPropertyType,
} from '@aloft/toolkit-shared';
import { Block, Typography, useModal } from '@aloft/toolkit-sui';

import type { KeyboardEvent, PropsWithChildren } from 'react';
import { Link } from 'react-router-dom';

import { ListCard } from '../../../../components/ListCard.tsx';
import { TkOrderListing } from '../OrderTable/types.ts';
import { OrderActionsMenu } from './OrderActionsMenu.tsx';
import { OrderCardDatum } from './OrderCardDatum.tsx';
import { OrderCardTags } from './OrderCardTags.tsx';
import { getBadgeFromOrder } from './utils/getBadgeFromOrder.ts';

export const Tkv2OrderCard = ({
  order,
  to,
  updateOrder,
  deleteOrder,
  downloadReport,
  openDocumentsPage,
}: {
  order: TkOrderListing;
  to: string;
  updateOrder: () => void;
  deleteOrder: () => void;
  downloadReport: () => void;
  openDocumentsPage: () => void;
}) => {
  const { Modal, openModal, isModalOpen, closeModal } = useModal();
  const clientTimezone = AloftTime.getCurrentTimeZone();
  const mlAddress = addressToMultilineAddress(order.address ?? {});
  const createdAt = order?.createdAt;
  const effectiveDate = order?.effectiveDate;
  const openOrderDisabled = order.isCompleted || order.isPending;
  let primaryDate = '';
  if (order.isPending) {
    primaryDate = 'Due Date TBD';
  } else if (order.isMock) {
    primaryDate = 'None';
  } else if (order.isCompleted) {
    let signedAtFormatted = '';
    try {
      signedAtFormatted = order.signedAt?.toAloftDate().format('MM/dd/yyyy') ?? '';
    } catch {
      // signedAt may contain an unformattable value; leave blank rather than crash
    }
    primaryDate = `Signed ${signedAtFormatted}`;
  } else if (order.inspectifyOrder) {
    primaryDate = `Due ${order.dueDate?.format('MM/dd/yyyy') ?? ''}`;
  } else {
    primaryDate = `Effective ${effectiveDate?.format('MM/dd/yyyy') ?? ''}`;
  }
  const secondaryDate = order.inspectifyOrder
    ? `Assigned ${createdAt?.formatTime(clientTimezone, 'MM/dd/yyyy') ?? ''}`
    : `Created ${createdAt?.formatTime(clientTimezone, 'MM/dd/yyyy') ?? ''}`;

  const WrapperComponent = openOrderDisabled
    ? Block
    : (props: PropsWithChildren) => (
        <Link
          to={!order.isCraftsman ? to.replace('paired-cost/analysis', 'legacy-export') : to}
          {...props}
        />
      );

  const handleCardClick = () => {
    if (order.isPending) openModal();
  };

  const handleCardKeyDown = (e: KeyboardEvent) => {
    if (!order.isPending || (e.key !== 'Enter' && e.key !== ' ')) return;
    e.preventDefault();
    openModal();
  };

  return (
    <>
      <WrapperComponent>
        <ListCard
          className="orders-list-card"
          role={order.isPending ? 'button' : 'group'}
          tabIndex={order.isPending ? 0 : undefined}
          onClick={handleCardClick}
          onKeyDown={handleCardKeyDown}
          style={{ cursor: order.isPending || !openOrderDisabled ? 'pointer' : 'default' }}
        >
          <OrderCardDatum
            label="Address"
            textPrimary={mlAddress[0]}
            textSecondary={mlAddress[1]}
            badge={getBadgeFromOrder(order)}
          />
          <OrderCardDatum
            label="Type"
            textPrimary={formatShortenedPropertyType(order.propertyType) ?? ''}
          />
          <OrderCardDatum
            label="Appraisal Type"
            textPrimary={formatAppraisalType(order.appraisalType) ?? ''}
            textSecondary={order.orderNumber ? `ID ${order.orderNumber}` : ''}
          />
          <OrderCardDatum
            label="Client"
            textPrimary={order.clientName ?? ''}
            textSecondary={
              order.appraiserFee !== undefined ? order.appraiserFee.toString(true) : ''
            }
          />
          <OrderCardDatum
            label="Effective"
            textPrimary={primaryDate}
            textSecondary={secondaryDate}
          />
          <OrderCardTags tags={order?.tags || []} orderID={order.id} />
          <OrderActionsMenu
            orderID={order.id}
            address={mlAddress[0]}
            isArchived={order.isArchived}
            isTraining={order.isTraining}
            isCompleted={order.isCompleted}
            updateOrder={updateOrder}
            deleteOrder={deleteOrder}
            downloadReport={downloadReport}
            openDocumentsPage={openDocumentsPage}
            isInspectifyOrder={!!order.inspectifyOrder}
            isMock={!!order.isMock}
            isPending={!!order.isPending}
          />
        </ListCard>
      </WrapperComponent>
      {isModalOpen && (
        <Modal
          isOpen={isModalOpen}
          onClose={closeModal}
          primaryAction={{ text: 'OK', onClick: closeModal }}
          showCloseButton={false}
          title="Awaiting Inspection Report"
        >
          <Typography variant="body1">
            This assignment will be unlocked once the inspection report is received. You will be
            notified by email when it is available.
          </Typography>
        </Modal>
      )}
    </>
  );
};
